<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = defineProps<{ groupIdx: number }>();
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonePresetsStoneQuality.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, but - like itemV3StonesSetting.vue / itemV3StonesStoneSize.vue -
 * the variants are fetched per stone group via getItemVariants(itemIdx, "stones.<groupIdx>.stone_quality")
 * / getItemVariants(itemIdx, "stones.<groupIdx>.stone_type") instead of the fixed
 * "stones.0.stone_quality" / "stones.*.stone_type" idx used there.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone quality - stones.<groupIdx>.stone_quality
 * stone type - stones.<groupIdx>.stone_type
 */

const stoneQuality = ref({
  "stone_quality": {},
  "stone_type": {}
} as any)

const getStoneQualityData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneQuality.value.stone_quality = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_quality")
    stoneQuality.value.stone_type = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_type")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneQualityData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneQualityData(props.groupIdx)
}, {deep: true})

/**
 * stone type variants (transferred from itemV3StonePresetsStoneQuality.vue's "stone type variants"),
 * now rendered as cards (icon + title + subtitle) instead of a sliding switch-button bar.
 */

const stoneTypeVariants = computed(() => stoneQuality.value.stone_type)

// subtitles shown below the stone type title, matching the attached layout
// ("Diamant" / "Brillant", "Labor Diamant" / "Nachhaltig", "Zirkonia" / "Strahlend")
const stoneTypeSubtitles: Record<string, string> = {
  earth: "Brillant",
  lab: "Nachhaltig",
  zirconia: "Strahlend",
}

const getStoneTypeSubtitle = (idx: string) => stoneTypeSubtitles[idx?.toLowerCase()] || ""

// all stone type cards share the same "rif" stone quality icon as their image
const stoneTypeImageSrc = "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_quality/rif-icon-300.webp"
const stoneTypeImageSrcset = "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_quality/rif-icon-600.webp"

/**
 * quality variants (transferred from itemV3StonePresetsStoneQuality.vue's "color variants")
 */

const active = computed(() => stoneQuality.value.stone_quality[0])

const variantsList = computed(() => active.value?.values || [])

const matchedIdx = computed(() => variantsList.value.findIndex((v: any) => v.match))

/**
 * hover
 */

const hoverIdx = ref(-1)

const setHoverIdx = () => {
  if (matchedIdx.value !== -1 && variantsList.value.length >= matchedIdx.value) {
    hoverIdx.value = matchedIdx.value
  }
}

watch(active, () => {
  setHoverIdx()
})

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

</script>

<template>
  <div v-if="active" class="w-full my-2">
    <div class="flex flex-wrap justify-center">
      <div v-if="stoneTypeVariants.length >= 1"
           class="shrink-0 border-b border-gray-300 pb-2 mb-2 md:border-b-0 2xl:border-r md:pb-0 md:mb-0 md:pr-2 md:mr-2">
        <ul class="flex gap-x-1 flex-wrap mb-2">
          <!-- stone type cards -->
          <li v-for="(stoneType, stoneTypeIdx) in stoneTypeVariants[0].values" :key="stoneTypeIdx"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              :class="{
                'border-2 border-brand-main bg-[#f3f0e9]': stoneType.match,
                'border-2 border-transparent': !stoneType.match
              }"
              @click="handleValuePatch(stoneTypeVariants[0].idx, stoneType)"
          >
            <div class="w-10 h-10 rounded-2xl flex">
              <img
                  :src="stoneTypeImageSrc"
                  :srcset="stoneTypeImageSrcset"
                  class="object-cover"
              >
            </div>
            <span class="text-parameter-title text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
              {{ stoneType.title }}
            </span>
          </li>
        </ul>
      </div>

      <div class="shrink-0 min-w-0">
        <ul class="w-full flex gap-x-1 flex-wrap mb-2 justify-center md:justify-end">
          <!-- quality list -->
          <li v-for="(variantData, variantIdx) in variantsList" :key="variantIdx"
              class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
              @click="handleValuePatch(active.idx, variantData)"
              @mouseover="hoverIdx = variantIdx as number"
              @mouseleave="hoverIdx = matchedIdx"
              :class="{
                'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
                'border-2 border-transparent': !variantData.match
              }"
          >
            <div
                class="w-10 h-10 rounded-2xl flex">
              <img
                  :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_quality/${variantData.idx.toLowerCase()}-icon-300.webp`"
                  :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_quality/${variantData.idx.toLowerCase()}-icon-600.webp`"
                  class="object-cover"
              >

            </div>
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
              {{ variantData.title }}
              </span>
            <span class="text-parameter-title text-[10px] overflow-hidden max-w-15 mx-auto text-center mt-1">
              {{ $n(variantData.price, 'currency') }}
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
